import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiRefreshCw } from 'react-icons/fi';

const plans = [
  { id: 'free', title: 'Free', description: 'For that first spark of an idea.', cta: 'Start building' },
  { id: 'premium', title: 'Premium', description: 'For your next chapter of growth.', cta: 'Get Premium', featured: true },
  { id: 'enterprise', title: 'Enterprise', description: 'For bigger ideas and more projects.', cta: 'Get Enterprise' },
];
const limits = [['maxProjects', 'projects', 'project'], ['maxDocuments', 'legal documents', 'legal document'], ['maxShortLinks', 'smart links', 'smart link'], ['maxSupportModules', 'support modules', 'support module'], ['maxInternalAds', 'internal ads', 'internal ad'], ['maxLandingPages', 'landing pages', 'landing page']];

function formatPrice(pricing) {
  if (!Number.isFinite(pricing?.monthly) || pricing.monthly < 0) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: pricing.currency || 'USD', maximumFractionDigits: 2, minimumFractionDigits: 0 }).format(pricing.monthly);
  } catch {
    return null;
  }
}

export default function HomePricing({ user }) {
  const [data, setData] = useState([]);
  const [status, setStatus] = useState('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    setStatus('loading');
    const load = async () => {
      try {
        const response = await fetch('/api/auth/plan-limits', { signal: controller.signal });
        if (!response.ok) throw new Error('Plans unavailable');
        const result = await response.json();
        if (!Array.isArray(result.planLimits) || !result.planLimits.length) throw new Error('Plans unavailable');
        if (mounted) { setData(result.planLimits); setStatus('ready'); }
      } catch {
        if (mounted) setStatus('error');
      } finally {
        window.clearTimeout(timeout);
      }
    };
    load();
    return () => { mounted = false; controller.abort(); window.clearTimeout(timeout); };
  }, [attempt]);

  return (
    <section id="pricing" className="home-section home-pricing">
      <div className="home-container">
        <div className="home-section-heading home-reveal"><span className="home-eyebrow">03 / ROOM TO GROW</span><h2>Your ambition. Your pace.</h2><p>Start with your idea. Find the plan that fits what comes next.</p></div>
        <div className="home-pricing-grid" aria-busy={status === 'loading'}>
          {plans.map(plan => {
            const live = data.find(item => item?.plan === plan.id);
            const price = formatPrice(live?.pricing);
            return <article className={`home-plan ${plan.featured ? 'home-plan-featured' : ''}`} key={plan.id}>
              <div className="home-plan-title"><h3>{plan.title}</h3>{plan.featured && <span>For growing projects</span>}</div>
              <p>{plan.description}</p>
              <div className="home-plan-price">{status === 'loading' ? <span className="home-price-skeleton" aria-label="Loading price" /> : price ? <><strong>{price}</strong><span>{live.pricing.monthly === 0 ? '/ forever' : '/ month'}</span></> : <span className="home-price-unavailable">Pricing unavailable</span>}</div>
              <Link to={user ? '/dashboard' : '/register'} className={`home-button ${plan.featured ? '' : 'home-button-outline'}`}>{user ? 'Go to dashboard' : plan.cta}<FiArrowRight aria-hidden="true" /></Link>
              <div className="home-plan-divider" />
              <span className="home-mini-label">PLAN LIMITS</span>
              <ul>{limits.map(([key, label, singular]) => {
                const value = live?.limits?.[key];
                const known = Number.isFinite(value) && value >= -1;
                return <li key={key} className={known && value === 0 ? 'home-plan-excluded' : ''}>
                  {known && value !== 0 ? <FiCheck aria-hidden="true" /> : <span className="home-limit-dash" aria-hidden="true">—</span>}
                  <span>{known ? `${value === -1 ? 'Unlimited' : value} ${value === 1 ? singular : label}` : label}</span>
                  {!known && <span className="sr-only">: limit unavailable</span>}
                </li>;
              })}</ul>
            </article>;
          })}
        </div>
        <div className="home-pricing-note" role="status">
          {status === 'error' ? <><span>Live prices and limits are temporarily unavailable.</span><button type="button" onClick={() => setAttempt(value => value + 1)}><FiRefreshCw aria-hidden="true" /> Try again</button></> : status === 'loading' ? 'Fetching current prices and plan limits…' : 'Monthly pricing. Your plan, your workspace, your next big idea.'}
        </div>
      </div>
    </section>
  );
}
